import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../constants';

/**
 * Hook to manage Terms and Conditions acceptance status
 * Returns the status and functions to check/accept terms
 */
const useTermsAcceptance = (isAuthenticated = true) => {
  const [termsStatus, setTermsStatus] = useState({
    loading: true,
    needsAcceptance: false,
    currentVersion: null,
    acceptedVersion: null,
    acceptedAt: null,
    error: null
  });

  const checkTermsStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setTermsStatus(prev => ({ ...prev, loading: false, needsAcceptance: false }));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setTermsStatus(prev => ({ ...prev, loading: false, needsAcceptance: false }));
        return;
      }

      const response = await axios.get(`${API}/terms/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTermsStatus({
        loading: false,
        needsAcceptance: response.data.needs_acceptance,
        currentVersion: response.data.current_version,
        acceptedVersion: response.data.accepted_version,
        acceptedAt: response.data.accepted_at,
        error: null
      });
    } catch (error) {
      console.error('Failed to check terms status:', error);
      // Don't block the user if we can't check status
      setTermsStatus(prev => ({
        ...prev,
        loading: false,
        needsAcceptance: false,
        error: 'Failed to check terms status'
      }));
    }
  }, [isAuthenticated]);

  const markAccepted = useCallback(() => {
    setTermsStatus(prev => ({
      ...prev,
      needsAcceptance: false,
      acceptedVersion: prev.currentVersion,
      acceptedAt: new Date().toISOString()
    }));
  }, []);

  useEffect(() => {
    checkTermsStatus();
  }, [checkTermsStatus]);

  return {
    ...termsStatus,
    checkTermsStatus,
    markAccepted
  };
};

export default useTermsAcceptance;
